import { MsalProvider } from '@azure/msal-react';
import { AuthError } from '@azure/msal-browser';
import { msalInstance } from './msalInstance';
import { useState, useEffect, type ReactNode } from 'react';
import {
  acquireApiTokenSilent,
  recoverFromSilentTokenFailure,
  storeAuthError,
  isConsentError,
  AUTH_REDIRECT_KEY,
  CONSENT_ERROR_MESSAGE,
  GENERIC_AUTH_ERROR_MESSAGE,
} from './authSession';

interface AuthProviderProps {
  children: ReactNode;
}

let restoreNavigationStarted = false;

export function AuthProvider({ children }: Readonly<AuthProviderProps>) {
  const [isReady, setIsReady] = useState(false);

  const ensureValidSession = async () => {
    const activeAccount = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!activeAccount) return;
    msalInstance.setActiveAccount(activeAccount);
    try {
      await acquireApiTokenSilent(activeAccount);
    } catch (error) {
      await recoverFromSilentTokenFailure(error, activeAccount);
    }
  };

  useEffect(() => {
    msalInstance.initialize()
      .then(() => msalInstance.handleRedirectPromise())
      .then(async (response) => {
        if (restoreNavigationStarted) return;
        if (response?.account) {
          msalInstance.setActiveAccount(response.account);
          const redirectUrl = sessionStorage.getItem(AUTH_REDIRECT_KEY);
          sessionStorage.removeItem(AUTH_REDIRECT_KEY);
          if (redirectUrl) {
            restoreNavigationStarted = true;
            globalThis.location.replace(redirectUrl);
            return;
          }
        } else {
          await ensureValidSession();
        }
        if (restoreNavigationStarted) return;
        setIsReady(true);
      })
      .catch(async (error) => {
        sessionStorage.removeItem(AUTH_REDIRECT_KEY);
        if (error instanceof AuthError) {
          storeAuthError(isConsentError(error) ? CONSENT_ERROR_MESSAGE : GENERIC_AUTH_ERROR_MESSAGE);
        }
        await msalInstance.clearCache();
        setIsReady(true);
      });
  }, []);

  useEffect(() => {
    const FIFTY_MINUTES = 50 * 60 * 1000;
    const interval = setInterval(() => {
      const activeAccount = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
      if (!activeAccount) return;
      acquireApiTokenSilent(activeAccount).catch(() => {});
    }, FIFTY_MINUTES);

    return () => clearInterval(interval);
  }, []);

  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}
